import { createApi, fetchBaseQuery } from "@reduxjs/toolkit/query/react";
import { allCatUsingPaging } from "../api/ApiRoutesFile";

export const categoryApiSlice = createApi({
  reducerPath: "categoryApi",
  baseQuery: fetchBaseQuery({
    baseUrl: "https://castle-auction-production.up.railway.app/api/",
    prepareHeaders: (headers) => {
      headers.set("Content-Type", "application/json");
      const token = localStorage.getItem("auction_portal");
      if (token) {
        headers.set("x-auth-token", token);
      }
      return headers;
    },
  }),
  tagTypes: ["Categories"],
  endpoints: (builder) => ({
    categories: builder.query({
      query: ({ endpoint, id, params }) => {
        const url = id ? `${endpoint}/${id}` : endpoint;
        return {
          url,
          method: "GET",
          params: params || undefined,
        };
      },
      providesTags: (result) =>
        result
          ? [
              ...result.categories.map(({ _id }) => ({
                type: "Categories",
                id: _id,
              })),
              { type: "Categories", id: "LIST" },
            ]
          : [{ type: "Categories", id: "LIST" }],
    }),

    createCategory: builder.mutation({
      query: ({ endpoint, formData }) => ({
        url: endpoint,
        method: "POST",
        body: formData,
      }),
      // Wait for the API response before updating the cache
      async onQueryStarted(
        { endpoint, formData, lastId },
        { dispatch, queryFulfilled }
      ) {
        try {
          const { data } = await queryFulfilled; // wait for the real API response
          if (data && data.category && data.category._id) {
            // Update the paginated query cache (if using a page-based cache)
            dispatch(
              categoryApiSlice.util.updateQueryData(
                "categories",
                { endpoint: allCatUsingPaging, id: lastId },
                (draft) => {
                  // Insert the new lot at the beginning (top)
                  draft.categories.unshift(data.category);
                }
              )
            );
          }
        } catch (error) {
          console.error("Create failed:", error);
        }
      },
    }),

    updateCategory: builder.mutation({
      query: ({ endpoint, id, formData }) => ({
        url: `${endpoint}/${id}`,
        method: "PUT",
        body: formData,
      }),
      // Wait for API response before updating the cache
      async onQueryStarted(
        { endpoint, id, formData, lastId },
        { dispatch, queryFulfilled }
      ) {
        try {
          const { data } = await queryFulfilled; // wait for the API call to complete
          if (data && data.category) {
            // Update paginated cache if applicable
            dispatch(
              categoryApiSlice.util.updateQueryData(
                "categories",
                { endpoint: allCatUsingPaging, id: lastId },
                (draft) => {
                  const index = draft.categories.findIndex((a) => a._id === id);
                  if (index !== -1) {
                    draft.categories[index] = data.category;
                  }
                }
              )
            );
          }
        } catch (error) {
          console.error("Update failed:", error);
          // Handle rollback if needed
        }
      },
    }),

    deleteCategory: builder.mutation({
      query: ({ endpoint, id }) => ({
        url: `${endpoint}/${id}`,
        method: "PUT", // Use DELETE if your API supports it
      }),

      // Only update the cache after the API call succeeds
      async onQueryStarted({ endpoint, id }, { dispatch, queryFulfilled }) {
        try {
          const { data } = await queryFulfilled;

          if (data && data?.Category) {
            dispatch(
              categoryApiSlice.util.updateQueryData(
                "categories",
                { endpoint: allCatUsingPaging, id: 1 }, // Adjust the ID or params if dynamic
                (draft) => {
                  if (draft && draft.categories) {
                    draft.categories = draft.categories.filter(
                      (item) => item._id !== id
                    );
                  }
                }
              )
            );
          }
        } catch (error) {
          console.error("Delete failed:", error);
        }
      },
    }),

    // Add this new endpoint to force-refresh lot data if needed
    refreshCategories: builder.query({
      query: () => "allCatUsingPaging",
      providesTags: [{ type: "Lots", id: "LIST" }],
    }),
  }),
});

export const {
  useCategoriesQuery,
  useCreateCategoryMutation,
  useUpdateCategoryMutation,
  useDeleteCategoryMutation,
  useLazyCategoriesQuery,
  useLazyRefreshCategoriesQuery,
} = categoryApiSlice;
